<template>
  <Head :title="__('games.index.title')" />

  <Breadcrumbs :current-page="__('games.index.title')" />

  <section class="container mb-6 md:mb-12">
    <h1 class="text-3xl font-bold text-center mb-6">
      {{ __('games.index.title') }}
    </h1>

    <section class="container mb-6">
      <form
        class="w-full flex md:justify-end"
        @submit.prevent="filter"
      >
        <div class="flex w-1/2 md:w-1/4">
          <label class="block w-full">
            <span class="text-2xs text-blue-600 font-bold mb-1.5">
              {{ __('games.filter.compatibility.label') }}
            </span>
            <select
              v-model="form.compatibility"
              @change="filter"
            >
              <option :value="null">{{ __('games.filter.compatibility.all') }}</option>
              <option value="ios">{{ __('games.filter.compatibility.ios') }}</option>
              <option value="android">{{ __('games.filter.compatibility.android') }}</option>
              <option value="desktop">{{ __('games.filter.compatibility.desktop') }}</option>
            </select>
          </label>
        </div>
      </form>
    </section>

    <div class="flex flex-col items-center">
      <div class="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <div
          v-if="webshopSuggestions.length > 0"
          class="w-full col-span-2 xl:col-span-4 mb-6 md:my-12"
          :class="{
            'row-start-2': mobileGames.length === 0,
            'row-start-1': mobileGames.length > 0,
          }"
        >
          <WebshopSuggestions :webshop-suggestions="webshopSuggestions" />
        </div>

        <div
          v-for="game in mobileGames"
          :key="game.id"
          class="flex flex-col items-center shadow"
        >
          <div class="flex flex-col h-full mb-2.5 md:mb-3.5">
            <Link
              :href="route('frontend.mobile-games.show', game.id)"
              class="relative mb-4 md:mb-6"
            >
              <img
                :src="game.icon_url"
                :alt="game.name"
                :title="game.name"
              >

              <div class="absolute z-101 bg-black/75 top-2 right-2 rounded-lg p-2">
                <img
                  v-if="game.operating_systems.includes('ios')"
                  src="/images/apple.svg"
                  class="w-5 h-5"
                >
                <img
                  v-if="game.operating_systems.includes('android')"
                  src="/images/android.svg"
                  class="w-5 h-5"
                >
              </div>

              <template v-if="!game.is_available">
                <div class="absolute inset-0 bg-gray-900 opacity-70 z-101" />
                <div class="absolute inset-0 flex items-center justify-center text-white">
                  <h2 class="font-bold text-center">
                    {{ __('games.mobile_game.not_available') }}
                  </h2>
                </div>
              </template>
            </Link>

            <div class="flex items-center justify-center grow md:text-2xl font-bold text-center px-4">
              <span>{{ __('games.win-max', {reward: game.max_reward }) }}</span>
            </div>
          </div>

          <div class="flex flex-col items-center px-2 md:px-4 mb-4 md:w-4/5">
            <div class="w-full mb-2 md:mb-3.5">
              <BlueSmallButton
                v-if="game.is_available"
                :href="route('frontend.mobile-games.show', game.id)"
                :internal="true"
              >
                {{ __('games.play') }}
              </BlueSmallButton>
            </div>

            <Link
              :href="route('frontend.mobile-games.show', game.id)"
              class="text-xs md:text-base text-center"
            >
              {{ __('games.index.detail-link') }}
            </Link>
          </div>
        </div>

        <div
          v-for="game in games"
          :key="game.id"
          class="flex flex-col items-center shadow"
        >
          <div class="flex flex-col h-full mb-2.5 md:mb-3.5">
            <Link :href="route('frontend.games.show', game.slug)">
              <img
                :src="game.media.url"
                :alt="game.name"
                :title="game.name"
                class="mb-4 md:mb-6"
              >
            </Link>

            <div class="flex items-center justify-center grow md:text-2xl font-bold text-center px-4">
              <span v-if="game.slug === 'scratch'">{{ __('games.win-max', {reward: game.reward }) }}</span>
              <span v-else>{{ __('games.win', {reward: game.reward }) }}</span>
            </div>
          </div>

          <div class="flex flex-col items-center px-2 md:px-4 mb-4 md:w-4/5">
            <div class="w-full mb-2 md:mb-3.5">
              <FullscreenModal ref="game-modal">
                <template #modal-button="{ openModal }">
                  <BlueSmallButton @click="openModal">
                    {{ __('games.play') }}
                  </BlueSmallButton>
                </template>

                <template #modal-content>
                  <iframe
                    :src="game.url"
                    class="h-[100dvh] w-full"
                  />
                </template>
              </FullscreenModal>
            </div>

            <Link
              :href="route('frontend.games.show', game.slug)"
              class="text-xs md:text-base text-center"
            >
              {{ __('games.index.detail-link') }}
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {useForm} from '@inertiajs/vue3';
import Breadcrumbs from '../../Components/Breadcrumbs.vue';
import FullscreenModal from '../../Components/FullscreenModal.vue';
import BlueSmallButton from '../../Components/Buttons/BlueSmall.vue';
import WebshopSuggestions from '../../Components/WebshopSuggestions.vue';

export default {
  components: {
    WebshopSuggestions,
    Breadcrumbs,
    FullscreenModal,
    BlueSmallButton,
  },

  props: {
    games: {
      type: Array,
      required: true,
    },

    gamesUrl: {
      type: String,
      required: true,
    },

    webshopSuggestions: {
      type: Array,
      required: true,
    },

    mobileGames: {
      type: Array,
      required: true,
    },

    mobileGamesCompatibility: {
      type: [String, null],
      required: true,
    },
  },

  data() {
    return {
      form: useForm({
        compatibility: this.mobileGamesCompatibility,
      }),
    };
  },

  mounted() {
    window.addEventListener('message', this.handleMessage);
  },

  beforeUnmount() {
    window.removeEventListener('message', this.handleMessage);
  },

  methods: {
    handleMessage(event) {
      if (event.origin !== this.gamesUrl) {
        return;
      }

      if (event.data.type === 'close-modal') {
        for (const gameModal of this.$refs['game-modal']) {
          gameModal.closeModal();
        }
      }
    },

    filter() {
      this.form.get(this.route('frontend.games.index'), {
        preserveScroll: true,
        preserveState: true,
        only: ['mobileGames'],
      });
    },
  },
};
</script>
